// /src/components/hubspot-form/HubspotFormRenderer.tsx
import React, { useRef } from 'react';
import { Box, Button, Typography, Grid } from '@mui/material';
import { Formik, Form } from 'formik';
import { Loader } from '../../../../components';
import { object, mixed, string } from 'yup';
import { IHubspotForm, IHubspotField } from '../../../../models';
import { TextFieldAdapter } from './fields/text-field-adapter';
import { PhoneFieldAdapter } from './fields/phone-field-adapter';
import { DropdownFieldAdapter } from './fields/dropdown-field-adapter';
import { FileFieldAdapter } from './fields/file-field-adapter';
import { stripHtml } from '../../util';

interface HubspotFormRendererProps {
  formName: string;
  form: IHubspotForm;
  onSubmit: (values: Record<string, any>) => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  formRef?: React.RefObject<any>;
  hiddenFields?: string[];
}
export const HubspotFormRenderer: React.FC<HubspotFormRendererProps> = ({
  formName,
  form,
  onSubmit,
  isSubmitting = false,
  submitButtonText = 'Submit',
  formRef,
  hiddenFields = []
}) => {
  const internalFormikRef = useRef<any>(null);
  const formikRef = formRef || internalFormikRef;

  // Initial values
  const initialValues = form.fieldGroups.reduce<Record<string, any>>((acc, group) => {
    group.fields.forEach(field => {
      if (field.fieldType === 'file') {
        acc[field.name] = []; // File[] (even for single-file fields)
      } else {
        acc[field.name] = '';
      }
    });
    return acc;
  }, {});

  // Validation schema (with file support)
  const validationSchema = object(
    form.fieldGroups.reduce<Record<string, any>>((acc, group) => {
      group.fields.forEach(field => {
        if (field.fieldType === 'file') {
          const isMultiple = true;
          const maxFiles = isMultiple ? 10 : 1; // default rules for now
          // const maxSizeMB = 25;
          const allowed = ['image/', 'video/']; // prefix match on MIME

          let validator = mixed<File[]>().test('files-are-array', 'Invalid file selection', value =>
            Array.isArray(value)
          );

          // Always require file fields (HubSpot API bug workaround for now)
          // if (field.required) {
          validator = validator.test(
            'file-required',
            'At least 1 file is required',
            value => Array.isArray(value) && value.length > 0
          );
          // }

          // count
          validator = validator.test(
            'file-count',
            isMultiple ? `You can upload at most ${maxFiles} file(s)` : 'Only one file is allowed',
            value => {
              if (!Array.isArray(value)) return false;
              return value.length <= maxFiles;
            }
          );

          // No size limit for now
          // size
          // validator = validator.test(
          //   'file-size',
          //   `Each file must be smaller than ${maxSizeMB}MB`,
          //   value => {
          //     if (!Array.isArray(value)) return false;

          //     if (value.length === 0) return true;
          //     const limit = maxSizeMB * 1024 * 1024;
          //     return value.every(f => f.size <= limit);
          //   }
          // );

          // type
          validator = validator.test('file-type', 'Only images or videos are allowed', value => {
            if (!Array.isArray(value)) return false;

            if (value.length === 0) return true;
            return value.every(f => {
              const t = f.type || '';
              return allowed.some(prefix => t.startsWith(prefix));
            });
          });

          acc[field.name] = validator;
        } else {
          // text/email/phone/dropdown defaults
          let validator: any = string();
          if (field.required) {
            validator = validator.required(`${stripHtml(field.label) || field.name} is required`);
          }
          if (field.fieldType === 'email') {
            validator = validator.email('Invalid email address');
          }
          if (field.fieldType === 'phone') {
            // Set default digit limits from HubSpot validation or use reasonable defaults
            const minDigits = field.validation?.minAllowedDigits || 10;
            const maxDigits = field.validation?.maxAllowedDigits || 10;

            validator = validator
              .test('phone-format', 'Please enter a valid phone number', value => {
                if (!value) return !field.required;
                // Allow common phone number formats
                const phoneRegex = /^[+]?[\d\s()-]+$/;
                return phoneRegex.test(value);
              })
              .test(
                'phone-length',
                `Phone number must have between ${minDigits} and ${maxDigits} digits`,
                value => {
                  if (!value) return !field.required;
                  const digits = value.replace(/\D/g, '').length;
                  return digits >= minDigits && digits <= maxDigits;
                }
              );
          }
          acc[field.name] = validator;
        }
      });
      return acc;
    }, {})
  );

  // Dynamic renderer
  function DynamicFieldRenderer({ field }: { field: IHubspotField }) {
    // Hide fields if they're in the hiddenFields array (temporary fix for API bugs)
    if (hiddenFields.includes(field.name)) {
      return null;
    }

    switch (field.fieldType) {
      case 'single_line_text':
      case 'email':
        return <TextFieldAdapter field={field} />;
      case 'phone':
        return <PhoneFieldAdapter field={field} />;
      case 'dropdown':
        return <DropdownFieldAdapter field={field} />;
      case 'file':
        return <FileFieldAdapter field={field} />;
      default:
        return (
          <Typography variant="body1">
            Unsupported field type: <strong>{field.fieldType}</strong>
          </Typography>
        );
    }
  }

  return (
    <Box>
      <Typography
        variant="h2"
        fontWeight={700}
        sx={{ mb: 2, color: theme => theme.palette.primary.dark }}
      >
        {formName}
      </Typography>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ resetForm }) => (
          <Box position="relative">
            {isSubmitting && (
              <Loader type="overlay" position="centered">
                Submitting...
              </Loader>
            )}
            <Form noValidate>
              <Grid container spacing={1}>
                {form.fieldGroups.map(group =>
                  group.fields.map(field => {
                    // Layout logic:
                    const isFile = field.fieldType === 'file';
                    const gridSize = isFile ? 12 : 6; // file = full width, text/email = half

                    return (
                      <Grid key={field.name} item xs={12} md={gridSize}>
                        <DynamicFieldRenderer field={field} />
                      </Grid>
                    );
                  })
                )}
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    width: { xs: '100%', md: '275px' }
                  }}
                >
                  {submitButtonText}
                </Button>
              </Box>
            </Form>
          </Box>
        )}
      </Formik>
    </Box>
  );
};
